/**
 * ExecutionMonitoringTest - Test component for Phase 1 implementation
 *
 * This component demonstrates and tests all Phase 1 functionality:
 * - Type definitions
 * - Hooks (useLatestExecution, useExecutionHistory, useConnectorMetrics)
 * - Components (ExecutionStatusBadge, ExecutionSummary, MetricCard)
 *
 * Usage: Import this component temporarily in ConnectorDashboard to verify API integration
 */
import { useState } from 'react';
import {
  useLatestExecution,
  useExecutionHistory,
  useConnectorMetrics,
} from '@/hooks/useConnectorExecutions';
import {
  ExecutionStatusBadge,
  ExecutionSummary,
  ExecutionSummaryEmpty,
  MetricCard,
  MetricCardSkeleton,
} from './index';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Activity, CheckCircle2, XCircle, TrendingUp } from 'lucide-react';

export function ExecutionMonitoringTest() {
  const [connectorType, setConnectorType] = useState('google_ads');
  const [connectorName, setConnectorName] = useState('default');
  const [testEnabled, setTestEnabled] = useState(false);

  // Test hooks
  const {
    data: latestExecution,
    isLoading: latestLoading,
    error: latestError,
  } = useLatestExecution(connectorType, connectorName, testEnabled);

  const {
    data: historyData,
    isLoading: historyLoading,
    error: historyError,
  } = useExecutionHistory(connectorType, connectorName, 1, 10, testEnabled);

  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
  } = useConnectorMetrics(connectorType, connectorName, 30, testEnabled);

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <h1 className="text-3xl font-bold">
        Execution Monitoring - Phase 1 Test
      </h1>

      {/* Test Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Test Controls</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Input
              placeholder="Connector Type (e.g., google_ads)"
              value={connectorType}
              onChange={(e) => setConnectorType(e.target.value)}
              className="max-w-xs"
            />
            <Input
              placeholder="Connector Name (e.g., default)"
              value={connectorName}
              onChange={(e) => setConnectorName(e.target.value)}
              className="max-w-xs"
            />
            <Button onClick={() => setTestEnabled(!testEnabled)}>
              {testEnabled ? 'Stop Testing' : 'Start Testing'}
            </Button>
          </div>

          {testEnabled && (
            <p className="text-sm text-muted-foreground">
              Testing with connector: <code className="bg-muted px-2 py-1 rounded">{connectorType}/{connectorName}</code>
            </p>
          )}
        </CardContent>
      </Card>

      {testEnabled && (
        <>
          {/* ExecutionStatusBadge Examples */}
          <Card>
            <CardHeader>
              <CardTitle>ExecutionStatusBadge Component</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <ExecutionStatusBadge status="pending" />
                <ExecutionStatusBadge status="running" />
                <ExecutionStatusBadge status="success" />
                <ExecutionStatusBadge status="partial" />
                <ExecutionStatusBadge status="failed" />
                <ExecutionStatusBadge status="cancelled" />
                <ExecutionStatusBadge status="timeout" />
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <ExecutionStatusBadge status="success" size="sm" />
                <ExecutionStatusBadge status="success" size="md" />
                <ExecutionStatusBadge status="success" size="lg" />
              </div>
            </CardContent>
          </Card>

          {/* Latest Execution */}
          <Card>
            <CardHeader>
              <CardTitle>Latest Execution (useLatestExecution hook)</CardTitle>
            </CardHeader>
            <CardContent>
              {latestLoading && <div className="text-muted-foreground">Loading...</div>}
              {latestError && (
                <div className="text-destructive">
                  Error: {latestError instanceof Error ? latestError.message : 'Unknown error'}
                </div>
              )}
              {!latestLoading && !latestError && latestExecution && (
                <ExecutionSummary execution={latestExecution} />
              )}
              {!latestLoading && !latestError && !latestExecution && (
                <ExecutionSummaryEmpty />
              )}
            </CardContent>
          </Card>

          {/* Execution History */}
          <Card>
            <CardHeader>
              <CardTitle>Execution History (useExecutionHistory hook)</CardTitle>
            </CardHeader>
            <CardContent>
              {historyLoading && <div className="text-muted-foreground">Loading...</div>}
              {historyError && (
                <div className="text-destructive">
                  Error: {historyError instanceof Error ? historyError.message : 'Unknown error'}
                </div>
              )}
              {!historyLoading && !historyError && historyData && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Total executions: {historyData.total}
                  </p>
                  {historyData.executions.length === 0 && (
                    <p className="text-sm text-muted-foreground">No executions found</p>
                  )}
                  {historyData.executions.slice(0, 3).map((execution) => (
                    <ExecutionSummary
                      key={execution.execution_id}
                      execution={execution}
                    />
                  ))}
                  {historyData.executions.length > 3 && (
                    <p className="text-sm text-muted-foreground">
                      ... and {historyData.executions.length - 3} more
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Connector Metrics (useConnectorMetrics hook)</CardTitle>
            </CardHeader>
            <CardContent>
              {metricsLoading && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <MetricCardSkeleton />
                  <MetricCardSkeleton />
                  <MetricCardSkeleton />
                  <MetricCardSkeleton />
                </div>
              )}
              {metricsError && (
                <div className="text-destructive">
                  Error: {metricsError instanceof Error ? metricsError.message : 'Unknown error'}
                </div>
              )}
              {!metricsLoading && !metricsError && metrics && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <MetricCard
                    icon={Activity}
                    label="Total Executions"
                    value={metrics.total_executions}
                    description="Last 30 days"
                  />
                  <MetricCard
                    icon={CheckCircle2}
                    label="Successful"
                    value={metrics.successful}
                    description="Last 30 days"
                    valueColor="text-success"
                    iconColor="text-success"
                  />
                  <MetricCard
                    icon={XCircle}
                    label="Failed"
                    value={metrics.failed}
                    description="Last 30 days"
                    valueColor="text-destructive"
                    iconColor="text-destructive"
                  />
                  <MetricCard
                    icon={TrendingUp}
                    label="Success Rate"
                    value={`${metrics.success_rate.toFixed(1)}%`}
                    description="Last 30 days"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Raw Response Data (for debugging) */}
          <Card>
            <CardHeader>
              <CardTitle>Raw API Responses (Debug)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <details>
                <summary className="cursor-pointer font-medium mb-2">
                  Latest Execution Response
                </summary>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-64">
                  {JSON.stringify(latestExecution, null, 2)}
                </pre>
              </details>

              <details>
                <summary className="cursor-pointer font-medium mb-2">
                  History Response
                </summary>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-64">
                  {JSON.stringify(historyData, null, 2)}
                </pre>
              </details>

              <details>
                <summary className="cursor-pointer font-medium mb-2">
                  Metrics Response
                </summary>
                <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-64">
                  {JSON.stringify(metrics, null, 2)}
                </pre>
              </details>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
